import React, { FC, useEffect, useRef, useState } from "react";
import { IApplicant, IProjectDetails, IJob } from "../helper";
import { Button, Form, FormInstance, FormProps, Modal, Input } from "antd";
import EnrichEditor from "../../Editor";
import { emailEditorRules, getReminderEmailBody, getSubject } from "../../common/emailBody";
import { StatusType } from "../../common";

interface Props {
  data: IApplicant;
  job?: IJob;
  sendEmail: (id: string, email: string, subject: string) => void;
}

export const EmailRequestBtn: FC<Props> = ({ data, job, sendEmail }) => {
  const [form] = Form.useForm();
  const formRef = useRef<FormInstance<any>>(null);
  const { _id, secondInterviewDate, isEmailSent: initialIsEmailSent, status } = data;

  const [showModal, setShowModal] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(initialIsEmailSent ?? false);

  useEffect(() => {
    setIsEmailSent(initialIsEmailSent ?? false);
  }, [initialIsEmailSent]);

  useEffect(() => {
    if (data && job) {
      const text = getReminderEmailBody(data, job);
      form.setFieldValue("emailBody", text);
    }
  }, [data, job, form]);

  const submitEmail: FormProps<{
    emailBody: string;
  }>["onFinish"] = async (values) => {
    const subject = getSubject("reminder", data.name, data.position);
    await sendEmail(_id, values.emailBody, subject);
    setIsEmailSent(true);
    setShowModal(false);
  };

  if (isEmailSent) return <>Sent</>;

  // Show button only for scheduled interviews with INTERVIEWING_MANAGER status and when email hasn't been sent
  if (!secondInterviewDate || secondInterviewDate === "N/A" || status !== StatusType.INTERVIEWING_MANAGER) return null;
  
  // Show button for scheduled interviews
  return (
    <>
      <Button type="primary" onClick={() => setShowModal(true)}>
        Send Email
      </Button>
      {showModal && (
        <Modal
          open
          onCancel={() => setShowModal(false)}
          onOk={() => formRef.current?.submit()}
          title="Edit Email Body"
          centered
          okText="Send Email"
        >
          <div style={{ margin: "20px 0" }}>
            <Form
              onFinish={submitEmail}
              ref={formRef}
              form={form}
              layout="vertical"
            >
              <Form.Item
                name="emailSubject"
                label="Subject"
                initialValue={getSubject("schedule", data.name, data.position)}
                rules={[
                  { required: true, message: "Subject is required" },
                  { max: 100, message: "Subject must be under 100 characters" },
                ]}
              >
                <Input placeholder="Enter email subject" />
              </Form.Item>

              <Form.Item name="emailBody" rules={emailEditorRules}>
                <EnrichEditor placeholder="Type reminder email for the Candidate and the Hiring Manager" />
              </Form.Item>
            </Form>
            <div style={{ marginBottom: 10, fontStyle: "italic" }}>
              <strong style={{ color: "red" }}>Disclaimer: </strong>
              This email will be sent to the candidate and the hiring manager.
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default EmailRequestBtn;
